import { useFieldArray } from 'react-hook-form';
import { maskDecimal, unmaskNumber } from '@givebutter/util';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import { InstallmentItem } from 'assets/js/pages/pledges/InstallmentItem';
import { useInstallmentCalculation } from 'assets/js/pages/pledges/useInstallmentCalculation';
import { InstallmentStatus, PledgeFormValue } from 'assets/js/pages/pledges/usePledgeForm';
import * as Form from 'components/form';
import { InstallmentFrequency } from 'constants/pledges';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { dateToServerDateTime } from 'helpers/dates';
import { DateTime } from 'luxon';

const Installments = () => {
    const methods = Form.useGivebutterFormContext<PledgeFormValue>();
    const { control, watch, getValues, path, setError, clearErrors } = methods;

    const installmentsArray = useFieldArray({
        name: 'installments',
        control,
    });

    const { fields: installments } = installmentsArray;

    const [totalPledge, frequency, startDate] = watch([
        'amount',
        'installment_frequency',
        'first_installment_date',
    ]);

    const { getInstallments, appendInstallment, removeInstallment } =
        useInstallmentCalculation(methods);

    const total = unmaskNumber(totalPledge || '0');

    const onUpdateInstallment = (_value: string) => {
        const installments = getValues('installments');
        getInstallments({
            total,
            frequency: InstallmentFrequency.Custom,
            start_date: dateToServerDateTime((startDate ?? DateTime.now()).endOf('day')),
            installments: installments.map((item, i) => {
                return {
                    index: i,
                    amount: unmaskNumber(item.amount),
                    date: dateToServerDateTime(item.date.endOf('day')),
                    override: item.override || item.status === InstallmentStatus.Paid,
                };
            }),
            number_of_installments: installments.length,
        });
    };

    const validateInstallments = () => {
        const installments = getValues('installments');
        const uniqueDates = new Set(installments.map(item => item.date.toISO())).size;
        if (installments.length !== uniqueDates) {
            setError('installments', { message: 'Installment dates must be unique.' });
        } else {
            clearErrors('installments');
        }
    };

    return (
        <Collapsible.Root>
            <div className="bg-gray-25 flex flex-col gap-3 p-3">
                <div className="flex gap-3 whitespace-nowrap">
                    <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-500">Installments</div>
                        <div className="text-lg font-semibold">
                            {installments.length ? (
                                <span className="text-gray-900">{installments.length}</span>
                            ) : (
                                <span className="text-gray-300">0</span>
                            )}
                        </div>
                    </div>
                    {frequency !== 'custom' && (
                        <div className="flex flex-col">
                            <div className="text-sm font-medium text-gray-500">
                                Installment amount
                            </div>
                            <div className="text-lg font-semibold">
                                {installments?.[0]?.amount ? (
                                    <span className="text-gray-900">
                                        <CurrencySymbol />
                                        {maskDecimal(installments?.[0]?.amount)}
                                    </span>
                                ) : (
                                    <span className="text-gray-300">
                                        <CurrencySymbol />
                                        {`0.00`}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-500">Total pledge</div>
                        <div className="text-lg font-semibold">
                            {installments.length ? (
                                <span className="text-gray-900">
                                    <CurrencySymbol />
                                    {totalPledge}
                                </span>
                            ) : (
                                <span className="text-gray-300">
                                    <CurrencySymbol />
                                    0.00
                                </span>
                            )}
                        </div>
                    </div>
                    {!!installments.length && (
                        <Collapsible.Trigger className="group ml-auto flex flex-col justify-end p-1">
                            <span className="whitespace-nowrap text-sm font-medium text-blue-500">
                                View installments{' '}
                                <FontAwesomeIcon
                                    className="group-radix-state-open:rotate-180"
                                    icon={faAngleDown}
                                />
                            </span>
                        </Collapsible.Trigger>
                    )}
                </div>
                {!!installments.length && (
                    <Collapsible.Content className="flex flex-col gap-4">
                        <div className="flex flex-col gap-4">
                            {installments.map((item, index) => (
                                <InstallmentItem
                                    canRemove={installments.length > 1}
                                    key={item.id}
                                    index={index}
                                    onRemove={removeInstallment}
                                    onAppend={appendInstallment}
                                    onAmountChange={onUpdateInstallment}
                                    onDateChange={() => validateInstallments()}
                                />
                            ))}
                        </div>
                        <Form.Error name={path('installments')} />
                    </Collapsible.Content>
                )}
            </div>
        </Collapsible.Root>
    );
};

export { Installments };
